import { Box, Collapse, Group, Paper, Stack, Text } from '@mantine/core'
import { IconChevronRight } from '@tabler/icons-react'
import type { ComponentType, ReactNode } from 'react'
import { useMemo, useState } from 'react'

import { theme } from '@/configs/theme'
import * as classes from '@/styles/CollapsibleContentSection.css'

type CollapsibleHelpSectionProps = {
  title?: ReactNode
  ContentComponent?: ComponentType
  ContentElement?: ReactNode
  /**
   * Whether the section is initially opened
   * @default false
   */
  defaultCollapsed?: boolean
  skipTimelineBullet?: boolean
  hidden?: boolean
  onToggle?: (nextCollapsed: boolean) => void
}

export function CollapsibleHelpSection(props: CollapsibleHelpSectionProps) {
  const { title, ContentComponent, ContentElement = null, defaultCollapsed = false, onToggle } = props
  const [collapsed, setCollapsed] = useState(defaultCollapsed ?? false)

  const controls = useMemo(
    () => ({
      open: () => setCollapsed(false),
      close: () => setCollapsed(true),
      toggle: () => setCollapsed(prevState => !prevState),
    }),
    [],
  )

  return (
    <Paper radius="md" shadow="xs">
      {!!title && (
        <Group
          onClick={() => {
            onToggle?.(!collapsed)
            controls.toggle()
          }}
          className={theme.cx(classes.groupHeader, classes.groupHeaderCollapsible)}>
          <Stack gap={0}>
            <Text size="md" fw={600}>
              {title}
            </Text>
          </Stack>

          <IconChevronRight
            className={theme.cx([classes.groupHeaderCaret, !collapsed && classes.groupHeaderCaretRotated])}
            stroke={1.5}
          />
        </Group>
      )}

      <Collapse expanded={!collapsed}>
        <Box px="lg" pb="md">
          {ContentComponent ? <ContentComponent /> : ContentElement}
        </Box>
      </Collapse>
    </Paper>
  )
}
